import { promotionalOfferCrudConfig } from '@/config/objects/promotional-offers';
import {
  Button,
  CrudFormView,
  FormPageLayout,
  toast,
  useApiClient,
  type CrudFormViewRef,
} from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Hotel-scoped promotional-offer form at `/hotels/:id/offers/new` (create) and
 * `/hotels/:id/offers/:offerId` (edit/view). Rendered as a full page using the
 * shared {@link FormPageLayout} (top bar + scrollable content + footer) so it
 * matches the global offer form and the amenity form.
 *
 * Reuses the shared {@link promotionalOfferCrudConfig} form so the fields match
 * the global offer form, but keeps the user inside the hotel: a successful create
 * links the new offer to the hotel, and every exit returns to the Offers tab.
 */
export function HotelOfferFormPage() {
  const { id, offerId } = useParams<{ id: string; offerId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const formRef = useRef<CrudFormViewRef>(null);

  const itemId = offerId;
  const isEdit = !!itemId;

  const backToOffers = () => navigate(`/hotels/${id}/offers`);

  const handleSuccess = async (offer: { id: string }) => {
    // A brand-new offer isn't linked to anything yet — attach it to this hotel
    // before returning so it shows up in the list. Edits are already linked.
    if (!isEdit) {
      try {
        await apiClient.post(`/hotels/${id}/promotional-offers`, {
          promotionalOfferId: offer.id,
        });
        toast.success('Promotional offer added to hotel');
      } catch {
        toast.error('Offer created, but linking it to the hotel failed');
      }
    }
    backToOffers();
  };

  const handleSave = () => formRef.current?.submit();

  if (!id) return null;

  const actions = (
    <>
      <Button type="button" variant="ghost" onClick={backToOffers}>
        Cancel
      </Button>
      <Button type="button" onClick={handleSave} loading={formRef.current?.isPending} icon={Save}>
        {isEdit ? 'Save Changes' : 'Create Offer'}
      </Button>
    </>
  );

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={backToOffers}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-xl font-semibold text-zinc-900">
            {isEdit ? 'Edit Promotional Offer' : 'Create Promotional Offer'}
          </h1>
        </div>
        <div className="flex items-center gap-3">{actions}</div>
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        <CrudFormView
          ref={formRef}
          config={promotionalOfferCrudConfig}
          itemId={itemId}
          onSuccess={handleSuccess}
          renderHeader={false}
          renderFooter={false}
        />
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={formRef.current?.lastSaved}>
        <div className="flex items-center gap-3">{actions}</div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
